/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuroraBackground } from "@/components/AuroraBackground";
import { LocaleLink } from "@/components/LocaleLink";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { track } from "@/lib/analytics";
import { ChevronLeft, ChevronRight, Heart, Volume2, Square } from "lucide-react";

const SPEECH_LANG = { en: "en-US", de: "de-DE", es: "es-ES", pt: "pt-BR", zh: "zh-CN" };

const KnowledgeArticlePage = () => {
  const { slug } = useParams();
  const { t, i18n, ready } = useTranslation(["guides", "site", "landing"]);
  const [speaking, setSpeaking] = useState(false);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);

  // Reset speech + load any stored rating when the article changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    try {
      setRating(Number(window.localStorage.getItem(`aurora-guide-rating-${slug}`)) || 0);
    } catch {
      setRating(0);
    }
  }, [slug]);

  // Stop reading when leaving the page.
  useEffect(() => () => {
    if (typeof window !== "undefined") window.speechSynthesis?.cancel();
  }, []);

  // Try the current language, then fall back to English, else show the "coming soon" stub.
  let article =
    (ready && i18n.getResource(i18n.language, "guides", slug)) ||
    (ready && i18n.getResource("en", "guides", slug)) ||
    null;

  if (!article || !article.sections) {
    article = {
      title: t("site:knowledgeArticle.fallbackTitle", "Guide coming soon"),
      intro: t(
        "site:knowledgeArticle.fallbackIntro",
        "We're polishing this guide. In the meantime, the Aurora app already includes daily prompts and journeys built around this topic."
      ),
      sections: [
        {
          h: t("site:knowledgeArticle.fallbackSectionH", "Get the practical version"),
          p: t(
            "site:knowledgeArticle.fallbackSectionP",
            "Aurora turns the ideas in our guides into personalized daily moments, scripts, and rituals you can act on tonight."
          ),
        },
      ],
    };
  }

  return (
    <>
      <section className="relative pb-8">
        <AuroraBackground variant="soft" />
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 pt-6 lg:pt-10">
          <LocaleLink to="/knowledge" className="inline-flex items-center gap-1 text-white/55 hover:text-white text-sm">
            <ChevronLeft size={14} /> {t("site:knowledgeArticle.back")}
          </LocaleLink>
          <Reveal>
            <h1 className="mt-6 font-serif text-[2.2rem] sm:text-5xl tracking-tight font-light leading-[1.05]">
              {article.title}
            </h1>
            <p className="mt-5 text-white/72 text-lg font-light leading-relaxed">
              {article.intro}
            </p>
            <button
              type="button"
              onClick={() => {
                if (typeof window === "undefined" || !window.speechSynthesis) return;
                if (speaking) {
                  window.speechSynthesis.cancel();
                  setSpeaking(false);
                  return;
                }
                const text = [article.title, article.intro, ...article.sections.flatMap((s) => [s.h, s.p])]
                  .filter(Boolean)
                  .join(". ");
                const utt = new SpeechSynthesisUtterance(text);
                utt.lang = SPEECH_LANG[(i18n.language || "en").slice(0, 2)] || "en-US";
                utt.onend = () => setSpeaking(false);
                utt.onerror = () => setSpeaking(false);
                window.speechSynthesis.cancel();
                window.speechSynthesis.speak(utt);
                setSpeaking(true);
                track("guide_listen", { slug });
              }}
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-white/85 hover:border-[var(--aurora-gold)]/60 hover:text-white transition-all"
              data-testid="guide-listen"
            >
              {speaking ? <Square size={14} className="text-[var(--aurora-gold)]" /> : <Volume2 size={16} className="text-[var(--aurora-gold)]" />}
              {speaking ? t("landing:listen.stop", "Stop") : t("landing:listen.start", "Listen")}
            </button>
          </Reveal>
        </div>
      </section>

      <article className="relative py-12">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 space-y-12">
          {article.sections.map((s, i) => (
            <Reveal key={`${slug}-${i}`} delay={i * 60}>
              <h2 className="font-serif text-2xl sm:text-3xl font-normal text-white/95">{s.h}</h2>
              <p className="mt-3 text-white/75 font-light leading-relaxed text-lg">{s.p}</p>
              {s.link && s.link.to ? (
                <LocaleLink
                  to={s.link.to}
                  className="mt-4 inline-flex items-center gap-1.5 text-[var(--aurora-cyan)] hover:gap-2.5 transition-all"
                >
                  {s.link.label} <ChevronRight size={15} />
                </LocaleLink>
              ) : null}
            </Reveal>
          ))}
        </div>
      </article>

      {/* GUIDE RATING */}
      <section className="relative py-8">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <div className="aurora-glass p-7 text-center">
              <p className="font-serif text-xl text-white/90">
                {rating
                  ? t("site:knowledgeArticle.rateThanks", "Thank you — this helps us write better guides.")
                  : t("site:knowledgeArticle.rateTitle", "Was this guide helpful?")}
              </p>
              <div className="mt-4 flex justify-center gap-2" onMouseLeave={() => setHovered(0)}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-label={`${n}/5`}
                    data-testid={`guide-rate-${n}`}
                    onMouseEnter={() => setHovered(n)}
                    onClick={() => {
                      setRating(n);
                      try {
                        window.localStorage.setItem(`aurora-guide-rating-${slug}`, String(n));
                      } catch { /* private mode — rating still tracked */ }
                      track("guide_rating", { slug, rating: n });
                    }}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Heart
                      size={26}
                      className={n <= (hovered || rating) ? "text-[var(--aurora-gold)]" : "text-white/25"}
                      fill={n <= (hovered || rating) ? "currentColor" : "none"}
                    />
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl tracking-tight font-light">
              {t("site:knowledgeArticle.closingTitle1")}{" "}
              <em className="italic text-[var(--aurora-gold)]">{t("site:knowledgeArticle.closingTitle2")}</em>
            </h2>
            <p className="mt-4 text-white/72 font-light">{t("site:knowledgeArticle.closingBody")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup startLabel={t("site:common.startFree")} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default KnowledgeArticlePage;
